/**
 * TimezoneBanner - Displays timezone difference information
 *
 * Shows a warning banner when the job site timezone differs from the device timezone.
 * Used in order management and order intake flows to inform users about timezone differences.
 */

import {useTimezoneInformation} from '@hconnect/tuc-processes/hooks'
import {Alert} from '@hm/fundamental'
import {Language} from '@mui/icons-material'
import {Box} from '@mui/material'
import React from 'react'
import {useTranslation} from 'react-i18next'

export interface TimezoneBannerProps {
  /** The job site timezone (IANA format, e.g., 'Australia/Sydney') */
  jobSiteTimezone?: string | null
  /** The selected order date/time in ISO format */
  selectedDate?: string | null
  /** Optional callback for analytics tracking when banner is displayed */
  onBannerVisible?: () => void
  /** Optional data-test-id for testing */
  'data-test-id'?: string
}

/**
 * Format day difference for display
 */
const formatDayDifference = (dayDifference: number, t: (key: string) => string): string => {
  if (dayDifference > 0) {
    return ` (+${dayDifference} ${t('orderIntake.rmc.orderManagement.timezone.day')})`
  }
  if (dayDifference < 0) {
    return ` (${dayDifference} ${t('orderIntake.rmc.orderManagement.timezone.day')})`
  }
  return ''
}

/**
 * TimezoneBanner Component
 *
 * Displays a warning banner when there's a timezone difference between
 * the user's device and the job site. The banner shows the job site's
 * local time to help users understand when their order will be processed.
 *
 * @example
 * ```tsx
 * <TimezoneBanner
 *   jobSiteTimezone="Australia/Sydney"
 *   selectedDate="2024-01-15T08:00:00.000Z"
 * />
 * ```
 */
export const TimezoneBanner: React.FC<TimezoneBannerProps> = ({
  jobSiteTimezone,
  selectedDate,
  onBannerVisible,
  'data-test-id': dataTestId = 'timezone-banner'
}) => {
  const {t} = useTranslation()
  const {isTimezoneDifferent, currentTimes, timezones, dayDifference} = useTimezoneInformation({
    jobSiteTimezone,
    selectedDate
  })

  // Trigger analytics callback when banner becomes visible
  React.useEffect(() => {
    if (isTimezoneDifferent && onBannerVisible) {
      onBannerVisible()
    }
  }, [isTimezoneDifferent, onBannerVisible])

  if (!isTimezoneDifferent || !currentTimes || !timezones) {
    return null
  }

  const dayDiffText = formatDayDifference(dayDifference ?? 0, t)

  return (
    <Alert variant="warning" data-test-id={dataTestId}>
      <Box sx={{display: 'flex', gap: '12px', alignItems: 'flex-start'}}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          <Language sx={{fontSize: '20px', color: '#b45309'}} />
        </Box>
        <Box sx={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
          <Box component="span" sx={{fontWeight: 600}}>
            {t('orderIntake.rmc.orderManagement.timezone.title', 'Timezone difference')}
          </Box>
          <Box component="span">
            {t(
              'orderIntake.rmc.orderManagement.timezone.jobSiteLocalTime',
              'Selected time is in your local timezone. Jobsite local time now: {{time}} {{timezone}}{{dayDifference}}.',
              {
                time: currentTimes?.jobSiteTime,
                timezone: timezones?.jobSiteTimezone,
                dayDifference: dayDiffText
              }
            )}
          </Box>
        </Box>
      </Box>
    </Alert>
  )
}
